<script setup lang="ts">
import { format } from 'date-fns';
import { collection, orderBy, query } from 'firebase/firestore';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Tag from 'primevue/tag';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useCollection } from 'vuefire';
import DeleteSessionConfirmation from '@/components/sessions/DeleteSessionConfirmation.vue';
import { useDebugUser } from '@/composables/useDebugUser';
import { db } from '@/firebase';
import Container from '@/layouts/Container.vue';

definePage({
  name: 'sessions',
});

const router = useRouter();
const user = useDebugUser();

// Filter states
const sortBy = ref('date-desc');
const rows = ref(10);
const first = ref(0);

// For session deletion confirmation
const showDeleteDialog = ref(false);
const sessionToDelete = ref('');

// Computed query based on filters
const sessionsQuery = computed(() => {
  if (!user.value?.uid) return null;

  const collectionRef = collection(db, 'imports', user.value.uid, 'sessions');
  const constraints = [];

  // Add sorting
  const [sortDirection] = sortBy.value.split('-');
  constraints.push(orderBy('createdAt', sortDirection === 'desc' ? 'desc' : 'asc'));

  return query(collectionRef, ...constraints);
});

// Fetch sessions using VueFire
const { data: sessions, pending } = useCollection(sessionsQuery);

function viewReport(sessionId: string) {
  router.push(`/reports/${sessionId}`);
}

// Navigate to drill progress page
function viewDrillProgress(drillSetId: string) {
  router.push(`/drills/${drillSetId}/progress`);
}

function deleteSession(sessionId: string) {
  sessionToDelete.value = sessionId;
  showDeleteDialog.value = true;
}

function handleSessionDeleted() {
  // The session is already deleted from Firestore
  // VueFire will automatically update the sessions collection
}
</script>

<template>
  <Container>
    <template #title>
      Uploaded Sessions
    </template>
    <p class="text-gray-900">
      These are files that have been uploaded to SHOTMETRICS AI.
    </p>
    <div id="sessions-table" class="bg-white rounded-lg p-4 shadow-md">
      <h2 class="text-xl font-semibold mb-4 text-gray-900">
        Sessions
      </h2>

      <div v-if="pending" class="text-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl text-gray-900" />
        <p class="mt-2 text-gray-900">
          Loading sessions...
        </p>
      </div>

      <div v-else-if="sessions?.length === 0" class="text-center text-gray-900 py-8">
        No sessions found. Upload your first session to see detailed reports.
      </div>

      <DataTable
        v-else
        v-model:first="first"
        v-model:rows="rows"
        :value="sessions"
        :paginator="true"
        :rows-per-page-options="[10, 20, 50]"
        responsive-layout="scroll"
        :sort-order="sortBy === 'date-asc' ? 1 : -1"
        sort-field="recordedAt"
        paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      >
        <Column
          sortable
          field="filename"
          header="Filename"
          style="width: 20%"
        >
          <template #body="{ data }">
            <div class="flex flex-col">
              <span class="text-gray-900">{{ data.filename || 'Untitled Session' }}</span>
              <div v-if="data.drillPractice" class="mt-1">
                <Tag
                  value="Drill Practice"
                  severity="info"
                  class="text-xs"
                />
              </div>
            </div>
          </template>
        </Column>
        <Column
          sortable
          field="recordedAt"
          header="Recorded At"
          style="width: 15%"
        >
          <template #body="{ data }">
            <div class="flex flex-col">
              <span class="text-gray-900">{{ format(data.recordedAt.toDate(), 'MMM d, yyyy') }}</span>
              <span class="text-gray-900 text-sm">{{ format(data.recordedAt.toDate(), 'h:mm a') }}</span>
            </div>
          </template>
        </Column>
        <Column
          sortable
          field="createdAt"
          header="Uploaded At"
          style="width: 15%"
        >
          <template #body="{ data }">
            <div class="flex flex-col">
              <span class="text-gray-900">{{ format(data.createdAt.toDate(), 'MMM d, yyyy') }}</span>
              <span class="text-gray-900 text-sm">{{ format(data.createdAt.toDate(), 'h:mm a') }}</span>
            </div>
          </template>
        </Column>
        <Column
          sortable
          field="shots"
          header="Shots"
          style="width: 10%"
        >
          <template #body="{ data }">
            <span class="text-gray-900">{{ data.shots?.length || 0 }}</span>
          </template>
        </Column>
        <Column header="Drill Details" style="width: 20%">
          <template #body="{ data }">
            <div v-if="data.drillPractice" class="flex flex-col">
              <span class="text-gray-900 font-medium">{{ data.drillPractice.drillName }}</span>
              <Button
                v-if="data.drillPractice.drillSetId"
                label="View Progress"
                icon="pi pi-chart-line"
                class="p-button-sm p-button-text mt-1"
                @click="viewDrillProgress(data.drillPractice.drillSetId)"
              />
            </div>
            <span v-else class="text-gray-500 italic">Regular session</span>
          </template>
        </Column>
        <Column style="width: 10%" header="Actions">
          <template #body="{ data }">
            <div class="flex gap-2">
              <Button
                icon="pi pi-chart-bar"
                class="p-button-sm p-button-rounded p-button-secondary whitespace-nowrap"
                label="View Report"
                @click="viewReport(data.id)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-sm p-button-rounded p-button-danger whitespace-nowrap"
                label="Delete"
                @click="deleteSession(data.id)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Session Deletion Confirmation Dialog -->
    <DeleteSessionConfirmation
      :visible="showDeleteDialog"
      :session-id="sessionToDelete"
      @close="showDeleteDialog = false"
      @deleted="handleSessionDeleted"
    />
  </Container>
</template>

<style>
#sessions-table {
  --p-paginator-background: white;
  --p-datatable-header-cell-background: white;
  --p-datatable-header-cell-color: var(--color-gray-900);
  --p-datatable-header-cell-selected-color: var(--color-gray-900);
  --p-datatable-row-background: white;
  --p-datatable-row-hover-background: var(--color-gray-50);
  --p-datatable-row-selected-background: var(--color-gray-50);
  --p-datatable-row-selected-hover-background: var(--color-gray-50);
  --p-datatable-row-selected-focus-background: var(--color-gray-50);
  --p-datatable-row-selected-focus-hover-background: var(--color-gray-50);
  --p-datatable-row-selected-focus-hover-background: var(--color-gray-50);
  --p-paginator-nav-button-selected-color: var(--color-gray-900);
}
</style>
